import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_GeneralLedgerAccountRow,
    WB_GeneralLedgerRow,
    WB_MasterDataSetRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbGeneralLedger} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_General_Ledger, esWB_General_Ledger_Account, esWB_MasterData_Set
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {GeneralLedgerBOValidations} from "./GeneralLedgerBO.Validations";

export interface IGeneralLedgerLoadOptions {
    dbContext?: PAdbContext
}

const GeneralLedgerLoadOptions_default: IGeneralLedgerLoadOptions = {}

export class GeneralLedgerBO implements IBusinessObject
{
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_GeneralLedger: WB_GeneralLedgerRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New General Ledger'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_GeneralLedger = undefined;
    }

    get WB_GeneralLedger(): WB_GeneralLedgerRow
    {
        return this.drWB_GeneralLedger;
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating General Ledger");
        let internalOnComplete = self._OnLoadComplete = (async () => {
            try {
                self.LoadOptions = options || {};
                if ("dbContext" in self.LoadOptions)
                    self.dbContext = self.LoadOptions.dbContext;
                else
                    self.dbContext = new PAdbContext();

                if (this.dbContext == null) {
                    let sError = "Unable to determine dbContext for new General Ledger";
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                //Create record using dbContext
                let newGeneralLedgerRow: WB_GeneralLedgerRow = <WB_GeneralLedgerRow>await self.dbContext.createRecordWithKey(esWB_General_Ledger)

                newGeneralLedgerRow.setDefaults();
                self.drWB_GeneralLedger = newGeneralLedgerRow;
                //add this to all the BOs
                self.dbContext.add(esWB_General_Ledger, newGeneralLedgerRow);
                self.Identity.setAll(self.WB_GeneralLedger.cif.WB_GL_ID, self.WB_GeneralLedger.cif.GENERAL_LEDGER_ID, self.WB_GeneralLedger.cif.GENERAL_LEDGER_NAME);
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(self.WB_GeneralLedger, "WB_GL_ID", "GENERAL_LEDGER_ID", "GENERAL_LEDGER_NAME");

                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                await self.applyCreateOptions()
                self.Identity.setAll(self.WB_GeneralLedger.cif.WB_GL_ID, self.WB_GeneralLedger.cif.GENERAL_LEDGER_ID, self.WB_GeneralLedger.cif.GENERAL_LEDGER_NAME);

                return self;
            } catch (err) {

                self.LoadProgressProvider.exception(err)
                return self
            }
        })()

        return internalOnComplete
    }

    async applyCreateOptions() {
        const self = this

        let sId = self.CreateOptions?.sId || self.LoadOptions?.sId
        if (!_.isEmpty(sId))
            self.WB_GeneralLedger.cif.GENERAL_LEDGER_ID = sId

        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName
        if (!_.isEmpty(sName))
            self.WB_GeneralLedger.cif.GENERAL_LEDGER_NAME = sName

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet)
            if (mds) {
                self.WB_GeneralLedger.WB_MasterDataSetRow = mds
                self.WB_GeneralLedger.cif.WB_MDSET_ID = wbMasterDataSet
            }
        }

    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        var self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")");


        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("General Ledger already loaded.")
                resolve(self); // "Customer already loaded.";
                return;
            }

            self.LoadOptions = options || {};
            _.defaults(self.LoadOptions, GeneralLedgerLoadOptions_default)


            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for General Ledger.")
                return;
            }

            if('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any> self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = GeneralLedgerBO.createRecordResolver({dbContext: self.dbContext})
                recordResolverLoadPromise = GeneralLedgerBO.setupRecordResolver(recordResolver, [anId])
            }


            recordResolverLoadPromise
                .then(function () {
                    let aGeneralLedgerRow: any = recordResolver.get(esWB_General_Ledger, anId)

                    if (_.isEmpty(aGeneralLedgerRow)) {
                        error("General Ledger not Found");
                        return;
                    }

                    self.drWB_GeneralLedger = aGeneralLedgerRow;
                    self.Identity.setAll(self.WB_GeneralLedger.cif.WB_GL_ID, self.WB_GeneralLedger.cif.GENERAL_LEDGER_ID, self.WB_GeneralLedger.cif.GENERAL_LEDGER_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_GeneralLedger, 'WB_GL_ID','GENERAL_LEDGER_ID','GENERAL_LEDGER_NAME')
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.finish()
                    resolve(self)
                }).catch(function (err: any) {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
                return;
            });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            'Validating ' + self.WB_GeneralLedger.cif.GENERAL_LEDGER_NAME + '(' + self.WB_GeneralLedger.cif.GENERAL_LEDGER_ID + ')'
        )

        self.ValidateOptions = options || {};

        try {
            if (self.dbContext == undefined) {
                const sError = 'Unable to determine dbContext to validate General Ledger.';
                self.ValidateProgressProvider.exception(sError);
                throw sError;
            }

            await GeneralLedgerBOValidations.ValidateFields(self)

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false;
            } else {
                self._bIsValidated = true;
            }
            self.ValidateProgressProvider.finish();

        } catch (err) {
            self._bIsValidated = false;
            self.ValidateProgressProvider.exception(err);

        }
        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    async SaveUsing(bBatch: boolean, options?: {bSkipValidations?: boolean}): Promise<IBusinessObject> {
        const self = this;
        var wbGeneralLedger = <IwbGeneralLedger>self.drWB_GeneralLedger;
        this.SaveProgressProvider.start("Saving " + wbGeneralLedger.GENERAL_LEDGER_NAME + "(" + wbGeneralLedger.WB_GL_ID + ")");

        try {
            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false;
                const sError = 'Unable to determine dbContext to Save General Ledger.';
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            if(!options?.bSkipValidations) {
                await self.Validate({})

                if (!self.bIsValidated) {
                    self._bIsSaved = false;
                    self.SaveProgressProvider.finish();
                    return self
                }
            }

            await saveFunction(esWB_General_Ledger, self.drWB_GeneralLedger,{})
            await saveFunction(esWB_General_Ledger_Account, self.drWB_GeneralLedger.WB_GeneralLedgerAccountRows)

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                });
            } else {
                self._bIsSaved = true;
            }

            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);

        } finally {

        }

    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbGeneralLedger = <IwbGeneralLedger>self.drWB_GeneralLedger;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbGeneralLedger.GENERAL_LEDGER_NAME + "(" + wbGeneralLedger.WB_GL_ID + ")");
        return new Promise<IBusinessObject>( (resolve, error) => {

            // 1. Validate if we can delete the GeneralLedger

            // 2. If Valid, mark all the records for deletion.
            self.drWB_GeneralLedger.markDeleted()

            if(bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }
    //</editor-fold>

    //------------GENERAL LEDGER ACCOUNT---------------------------------------------------
//<editor-fold desc = "General Ledger Account">
    addGLAccount(): Promise<IBusinessObject> {

        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            var aGLAccountRow: WB_GeneralLedgerAccountRow = undefined

            self.dbContext.createRecordWithKey(esWB_General_Ledger_Account)
                .then((aNewRow: WB_GeneralLedgerAccountRow) => {
                    aNewRow.setDefaults()
                    aGLAccountRow = aNewRow
                    aGLAccountRow.cif.WB_GL_ID = self.drWB_GeneralLedger.cif.WB_GL_ID
                    self.dbContext.add(esWB_General_Ledger_Account, aGLAccountRow)
                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    removeGLAccount(aGLAccountId: number): Promise<IBusinessObject> {
        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            _.forEach(self.drWB_GeneralLedger.WB_GeneralLedgerAccountRows, (aGLAccountRow: WB_GeneralLedgerAccountRow) => {
                if(aGLAccountRow.isActive && aGLAccountRow.cif.WB_GLACCT_ID == aGLAccountId) {
                    aGLAccountRow.markDeleted()
                }
            })

            resolve(self)
        })
    }
//</editor-fold>

    //<editor-fold desc = "Record Resolver operations">
    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const wrksp_options: CreateOptions = {
            standardConditions: {
            },
            dctIncludeBelongsToNamedRelations: {
            }
        }

        const aRecResolver = new ESRecordResolver(dbContext, wrksp_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_General_Ledger,ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

//</editor-fold">

};
PAEnvironment.registerBusinessObjectClass( GeneralLedgerBO , "GeneralLedgerBO", "General Ledger")